import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Truck, X, Ban, SearchCheck } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { LUX_EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Processing", "Shipped", "Out for Delivery", "Delivered", "Cancelled"];

const statusColor = {
  New: "bg-gold/15 text-gold-light border-gold/40",
  Processing: "bg-blue-500/10 text-blue-200 border-blue-400/30",
  Shipped: "bg-teal-500/10 text-teal-200 border-teal-400/30",
  "Out for Delivery": "bg-amber-500/10 text-amber-200 border-amber-400/30",
  Delivered: "bg-green-500/10 text-green-200 border-green-400/30",
  Cancelled: "bg-red-500/10 text-red-200 border-red-400/30",
};

export function AdminCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    (async () => {
      const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
      try {
        const res = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) await res.text();
      } catch {
        /* handled by admin screen state */
      }
      navigate("/admin", { replace: true });
    })();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian">
      <span className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" aria-label="Loading" />
    </div>
  );
}

export default function AdminPage() {
  usePageMeta("Admin | KAZIM'S", "Order management");
  const [user, setUser] = useState(undefined);
  const [orders, setOrders] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [courierModal, setCourierModal] = useState(null); // order
  const [couriers, setCouriers] = useState([]);
  const [courierLoading, setCourierLoading] = useState(false);
  const [toast, setToast] = useState("");

  const load = async () => {
    const res = await fetch(`${API}/admin/orders`, { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      setOrders(data.orders || []);
    }
    return res.ok;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (res.ok) {
          setUser(await res.json());
          await load();
        } else {
          setUser(null);
        }
      } catch {
        setUser(null);
      }
    })();
  }, []);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setOrders([]);
  };

  const act = async (orderId, action, body = {}) => {
    setBusyId(orderId + action);
    try {
      const res = await fetch(`${API}/admin/orders/${orderId}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) setToast(data.detail || "Action failed");
      await load();
      return data;
    } catch (e) {
      setToast("Action failed");
    } finally {
      setBusyId("");
    }
  };

  const openCouriers = async (order) => {
    setCourierModal(order);
    setCouriers([]);
    setCourierLoading(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}/couriers`, { credentials: "include" });
      const data = await res.json();
      setCouriers(data.couriers || []);
    } catch {
      setCouriers([]);
    }
    setCourierLoading(false);
  };

  if (user === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian pt-20">
        <span className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" aria-label="Loading" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian px-6 pb-20 pt-32">
        <div className="w-full max-w-md border hairline bg-velvet/60 p-10 text-center">
          <Truck className="mx-auto h-10 w-10 text-gold" strokeWidth={1.25} />
          <h1 className="mt-6 font-serif text-3xl text-cream">Order Management</h1>
          <p className="mt-3 text-sm leading-relaxed text-cream/60">
            Sign in with the authorised KAZIM'S Google account to manage orders and shipments.
          </p>
          <button
            onClick={signIn}
            data-testid="admin-signin-button"
            className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-7 py-4 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
            Sign in with Google
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pb-24 pt-28 md:pt-32">
      <div className="mx-auto max-w-7xl px-4 lg:px-10 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-cream" data-testid="admin-heading">Order Management</h1>
            <p className="mt-1.5 text-sm text-cream/55">
              {orders.length} order{orders.length === 1 ? "" : "s"} · signed in as {user.email}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border hairline px-5 py-2.5 text-[12px] uppercase tracking-[0.15em] text-cream/70 transition-colors hover:border-gold/50 hover:text-gold"
            >
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border hairline px-5 py-2.5 text-[12px] uppercase tracking-[0.15em] text-cream/70 transition-colors hover:border-gold/50 hover:text-gold"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>

        {toast && (
          <p className="mt-6 border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold-light" data-testid="admin-toast">
            {toast}
            <button onClick={() => setToast("")} className="ml-3 underline" data-testid="admin-toast-dismiss">dismiss</button>
          </p>
        )}

        {orders.length === 0 ? (
          <div className="mt-12 border border-dashed border-gold/30 bg-velvet/30 p-12 text-center">
            <p className="font-serif text-2xl italic text-gold/80">No orders yet</p>
            <p className="mt-3 text-sm text-cream/55">
              Orders placed on the website appear here in real time.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-10 hidden overflow-x-auto border hairline md:block">
              <table className="w-full min-w-[980px] text-left text-sm" data-testid="admin-orders-table">
                <thead>
                  <tr className="border-b hairline bg-obsidian/60 text-[11px] uppercase tracking-[0.15em] text-gold/90">
                    <th className="px-4 py-4 font-medium">Order</th>
                    <th className="px-4 py-4 font-medium">Customer</th>
                    <th className="px-4 py-4 font-medium">Items / Value</th>
                    <th className="px-4 py-4 font-medium">Payment</th>
                    <th className="px-4 py-4 font-medium">Status</th>
                    <th className="px-4 py-4 font-medium">Shipment</th>
                    <th className="px-4 py-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-b hairline align-top transition-colors hover:bg-velvet2/40" data-testid={`admin-order-row-${o.order_id}`}>
                      <td className="px-4 py-5">
                        <p className="font-serif text-base tracking-[0.12em] text-gold-light">{o.order_id}</p>
                        <p className="mt-1 text-[12px] text-cream/45">
                          {new Date(o.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </td>
                      <td className="px-4 py-5 text-cream/75">
                        {o.customer.name}
                        <span className="block text-[12px] text-cream/45">{o.customer.phone}</span>
                        <span className="block text-[12px] text-cream/45">{o.customer.city}, {o.customer.state} - {o.customer.pincode}</span>
                      </td>
                      <td className="px-4 py-5 text-cream/75">
                        {o.items.map((it) => (
                          <span key={it.sku} className="block">
                            {it.name} × {it.units}
                          </span>
                        ))}
                        <span className="mt-1 block font-serif text-lg text-gold">₹{o.subtotal}</span>
                      </td>
                      <td className="px-4 py-5">
                        <span className={`inline-block border px-2.5 py-1 text-[11px] uppercase tracking-widest ${o.payment_method === "COD" ? "border-gold/40 bg-gold/10 text-gold-light" : "border-teal-400/30 bg-teal-500/10 text-teal-200"}`} data-testid={`admin-payment-badge-${o.order_id}`}>
                          {o.payment_method}
                        </span>
                        <span className="mt-1.5 block text-[11px] text-cream/45">{o.payment_status.replace("_", " ")}</span>
                      </td>
                      <td className="px-4 py-5">
                        <select
                          value={o.status}
                          onChange={(e) => act(o.order_id, "status", { status: e.target.value })}
                          data-testid={`admin-status-select-${o.order_id}`}
                          className={`border px-2.5 py-1.5 text-[12px] uppercase tracking-widest ${statusColor[o.status] || ""}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-obsidian text-cream">{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-5 text-[12px] text-cream/60">
                        {o.shiprocket.awb_code ? (
                          <>
                            <span className="block text-cream/85" data-testid={`admin-awb-${o.order_id}`}>AWB {o.shiprocket.awb_code}</span>
                            <span className="block">{o.shiprocket.courier_name || "—"}</span>
                            {o.tracking?.current_status && (
                              <span className="mt-1 block text-gold-light" data-testid={`admin-tracking-status-${o.order_id}`}>{o.tracking.current_status}</span>
                            )}
                          </>
                        ) : o.shiprocket.error ? (
                          <span className="block text-red-300" data-testid={`admin-sr-error-${o.order_id}`}>SR error — Sync to retry</span>
                        ) : (
                          <span className="block">Shipment pending</span>
                        )}
                        {o.shiprocket.order_id && <span className="mt-1 block text-cream/35">SR #{o.shiprocket.order_id}</span>}
                      </td>
                      <td className="px-4 py-5">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => openCouriers(o)}
                            data-testid={`admin-couriers-button-${o.order_id}`}
                            className="inline-flex items-center gap-2 border border-gold/40 px-3 py-1.5 text-[11px] uppercase tracking-widest text-gold transition-colors hover:bg-gold/10"
                          >
                            <Truck className="h-3.5 w-3.5" strokeWidth={1.5} />
                            Couriers
                          </button>
                          <button
                            onClick={() => act(o.order_id, "sync")}
                            disabled={busyId === o.order_id + "sync"}
                            data-testid={`admin-sync-button-${o.order_id}`}
                            className="inline-flex items-center gap-2 border hairline px-3 py-1.5 text-[11px] uppercase tracking-widest text-cream/70 transition-colors hover:border-gold/50 hover:text-gold"
                          >
                            <SearchCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                            Sync
                          </button>
                          {o.status !== "Cancelled" && o.status !== "Delivered" && (
                            <button
                              onClick={() => { if (window.confirm(`Cancel order ${o.order_id}?`)) act(o.order_id, "cancel"); }}
                              data-testid={`admin-cancel-button-${o.order_id}`}
                              className="inline-flex items-center gap-2 border border-red-400/30 px-3 py-1.5 text-[11px] uppercase tracking-widest text-red-300 transition-colors hover:bg-red-500/10"
                            >
                              <Ban className="h-3.5 w-3.5" strokeWidth={1.5} />
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-10 space-y-4 md:hidden">
              {orders.map((o) => (
                <div key={o.order_id} className="border hairline bg-velvet/40 p-5" data-testid={`admin-order-card-${o.order_id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-lg tracking-[0.12em] text-gold-light">{o.order_id}</p>
                    <span className={`border px-2.5 py-1 text-[11px] uppercase tracking-widest ${statusColor[o.status]}`}>{o.status}</span>
                  </div>
                  <p className="mt-2 text-[13px] text-cream/70">{o.customer.name} · {o.customer.phone}</p>
                  <p className="text-[12px] text-cream/45">{o.customer.city}, {o.customer.state} - {o.customer.pincode}</p>
                  <p className="mt-2 text-[13px] text-cream/70">{o.items[0].name} × {o.items[0].units} · <span className="text-gold">₹{o.subtotal}</span> · {o.payment_method}</p>
                  <p className="mt-2 text-[12px] text-cream/60">
                    {o.shiprocket.awb_code ? `AWB ${o.shiprocket.awb_code} · ${o.shiprocket.courier_name || ""}` : "Shipment pending"}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <select
                      value={o.status}
                      onChange={(e) => act(o.order_id, "status", { status: e.target.value })}
                      data-testid={`admin-status-select-mobile-${o.order_id}`}
                      className="border hairline bg-obsidian px-3 py-2 text-[12px] uppercase tracking-widest text-cream"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => openCouriers(o)} data-testid={`admin-couriers-button-mobile-${o.order_id}`} className="border border-gold/40 px-3 py-2 text-[11px] uppercase tracking-widest text-gold">Couriers</button>
                    <button onClick={() => act(o.order_id, "sync")} data-testid={`admin-sync-button-mobile-${o.order_id}`} className="border hairline px-3 py-2 text-[11px] uppercase tracking-widest text-cream/70">Sync</button>
                    {o.status !== "Cancelled" && o.status !== "Delivered" && (
                      <button onClick={() => { if (window.confirm(`Cancel order ${o.order_id}?`)) act(o.order_id, "cancel"); }} data-testid={`admin-cancel-button-mobile-${o.order_id}`} className="border border-red-400/30 px-3 py-2 text-[11px] uppercase tracking-widest text-red-300">Cancel</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {courierModal && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-obsidian/85 p-4 backdrop-blur-sm"
          onClick={() => setCourierModal(null)}
          data-testid="courier-modal"
        >
          <div
            className="w-full max-w-lg border hairline bg-velvet p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-cream">Couriers · {courierModal.order_id}</h3>
              <button onClick={() => setCourierModal(null)} data-testid="courier-modal-close-button" aria-label="Close" className="flex h-10 w-10 items-center justify-center border hairline text-cream/70 hover:text-gold">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <p className="mt-2 text-[13px] text-cream/55">
              {courierModal.customer.city} - {courierModal.customer.pincode} · {courierModal.payment_method}
            </p>
            {courierLoading ? (
              <div className="flex justify-center py-10">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
              </div>
            ) : couriers.length === 0 ? (
              <p className="mt-6 border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold-light" data-testid="courier-modal-empty">
                No serviceable couriers found. Check the pickup location and PIN in your Shiprocket panel.
              </p>
            ) : (
              <ul className="mt-6 max-h-80 space-y-3 overflow-y-auto pr-1">
                {couriers.map((c, i) => (
                  <li key={c.courier_id} className="flex items-center justify-between border hairline bg-obsidian/40 p-4" data-testid={`courier-option-${i}`}>
                    <div>
                      <p className="text-cream" data-testid={`courier-name-${i}`}>{c.name}</p>
                      <p className="text-[12px] text-cream/50">₹{c.rate} · ETA {c.etd ?? "—"}</p>
                    </div>
                    <button
                      onClick={async () => {
                        await act(courierModal.order_id, "assign", { courier_id: c.courier_id });
                        setCourierModal(null);
                      }}
                      data-testid={`courier-assign-button-${i}`}
                      className="bg-gold px-4 py-2 text-[11px] font-medium uppercase tracking-widest text-obsidian transition-colors hover:bg-gold-light"
                    >
                      Assign
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
